import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navigationItems = [
    { label: 'Home', path: '/homepage', icon: 'Home' },
    { label: 'How It Works', path: '/homepage#how-it-works', icon: 'HelpCircle' },
    { label: 'Pricing', path: '/homepage#pricing', icon: 'DollarSign' },
    { label: 'About', path: '/about', icon: 'Info' },
    { label: 'Blog', path: '/blog', icon: 'BookOpen' },
    { label: 'Contact', path: '/contact', icon: 'MessageCircle' }
  ];

  const isActiveLink = (path) => {
    if (path?.includes('#')) {
      return location?.pathname === path?.split('#')?.[0];
    }
    return location?.pathname === path;
  };

  const handleLinkClick = (path) => {
    if (path?.includes('#')) {
      const [pathname, hash] = path?.split('#');
      if (location?.pathname === pathname) {
        const element = document.getElementById(hash);
        if (element) {
          element?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header 
        className={`sticky top-0 z-100 w-full transition-all duration-300 ${
          isScrolled 
            ? 'glass-morphism shadow-elevation' 
            : 'bg-background/95 backdrop-blur-sm'
        }`}
      >
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link 
              to="/homepage" 
              className="flex items-center space-x-3 transition-micro hover:opacity-80"
            >
              <Image
                src="/assets/images/image-1760433114854.png"
                alt="Workfolio logo featuring two circular figures representing people within a circular border, next to bold WORKFOLIO text in dark blue"
                className="h-8 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => handleLinkClick(item?.path)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-micro ${
                    isActiveLink(item?.path)
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  {item?.label}
                </Link>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-3">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/sign-in">Sign In</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to="/role-selection-modal">Get Started</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-micro"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={24} />
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-90 md:hidden">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-card shadow-elevation-lg animate-slide-in">
            <div className="flex h-16 items-center justify-between px-6 border-b border-border">
              <Image
                src="/assets/images/image-1760433114854.png"
                alt="Workfolio logo featuring two circular figures representing people within a circular border, next to bold WORKFOLIO text in dark blue"
                className="h-6 w-auto"
              />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-micro"
                aria-label="Close menu"
              >
                <Icon name="X" size={24} />
              </button>
            </div>
            
            <nav className="flex flex-col p-6 space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => handleLinkClick(item?.path)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-micro ${
                    isActiveLink(item?.path)
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </Link>
              ))}
              
              <div className="pt-6 mt-6 border-t border-border space-y-3">
                <Button variant="ghost" fullWidth asChild>
                  <Link to="/sign-in">Sign In</Link>
                </Button>
                <Button fullWidth asChild>
                  <Link to="/role-selection-modal">Get Started</Link>
                </Button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;